/**
 * F14.1 — ESTADOS DE SINCRONIZACIÓN PARA EL USUARIO Y POLÍTICA DE REINTENTOS
 *
 * Los estados técnicos de la cola (`db.ts::EstadoSyncMutacion`) no cambian: se TRADUCEN aquí, en un único sitio,
 * para que ninguna pantalla vuelva a inventar su propio vocabulario («Pendiente de enviar» en un sitio y
 * «Pendiente de sincronizar» en otro, como pasaba).
 *
 *   PENDIENTE                         → Guardado en este dispositivo · pendiente de sincronizar
 *   SINCRONIZANDO                     → Sincronizando
 *   EN_ESPERA (DEPENDENCIA_BLOQUEADA) → Esperando otra operación
 *   EN_ESPERA (CONFLICTO_REVISION)    → Necesita revisión
 *   ERROR_REINTENTABLE                → Error temporal · se reintentará  (agotados los automáticos: Error temporal · reintenta)
 *   RECHAZADA                         → Necesita revisión
 *   SINCRONIZADA                      → Guardado en el servidor
 */
import type { Mutacion } from './db.ts';

export type EstadoHumano =
  | 'PENDIENTE'
  | 'SINCRONIZANDO'
  | 'ESPERANDO_OTRA'
  | 'ERROR_TEMPORAL'
  | 'NECESITA_REVISION'
  | 'SINCRONIZADO';

export interface PresentacionEstado {
  readonly estado: EstadoHumano;
  readonly texto: string;
  /** Clase de `.etiqueta` del sistema de diseño. */
  readonly clase: 'neutra' | 'info' | 'aviso' | 'error' | 'exito-suave';
}

/** Reintentos AUTOMÁTICOS de una operación con error temporal; después, sólo manual («Reintentar»). */
export const MAX_REINTENTOS_AUTOMATICOS = 5;
const BASE_ESPERA_MS = 30_000;
const MAXIMO_ESPERA_MS = 15 * 60_000;

/** Espera antes del siguiente reintento automático: 30 s, 1 min, 2 min, 4 min… hasta 15 min. */
export function esperaReintento(intentos: number): number {
  if (intentos <= 0) return 0;
  return Math.min(MAXIMO_ESPERA_MS, BASE_ESPERA_MS * 2 ** (intentos - 1));
}

/** ¿Puede ir en una ronda AUTOMÁTICA ahora? (Una ronda manual envía todas las PENDIENTE y ERROR_REINTENTABLE.) */
export function sincronizableAutomaticamente(m: Pick<Mutacion, 'estadoSync' | 'intentos' | 'ultimoIntentoEn'>, ahora = Date.now()): boolean {
  if (m.estadoSync === 'PENDIENTE') return true;
  if (m.estadoSync !== 'ERROR_REINTENTABLE') return false;
  if (m.intentos >= MAX_REINTENTOS_AUTOMATICOS) return false;
  const ultimo = m.ultimoIntentoEn == null ? 0 : Date.parse(m.ultimoIntentoEn);
  return ahora >= ultimo + esperaReintento(m.intentos);
}

export function presentarEstado(m: Pick<Mutacion, 'estadoSync' | 'razonEspera' | 'intentos'>): PresentacionEstado {
  switch (m.estadoSync) {
    case 'PENDIENTE':
      return { estado: 'PENDIENTE', texto: 'Guardado en este dispositivo · pendiente de sincronizar', clase: 'neutra' };
    case 'SINCRONIZANDO':
      return { estado: 'SINCRONIZANDO', texto: 'Sincronizando…', clase: 'info' };
    case 'EN_ESPERA':
      return m.razonEspera === 'DEPENDENCIA_BLOQUEADA'
        ? { estado: 'ESPERANDO_OTRA', texto: 'Esperando otra operación', clase: 'aviso' }
        : { estado: 'NECESITA_REVISION', texto: 'Necesita revisión', clase: 'aviso' };
    case 'ERROR_REINTENTABLE':
      return m.intentos >= MAX_REINTENTOS_AUTOMATICOS
        ? { estado: 'ERROR_TEMPORAL', texto: 'Error temporal · pulsa «Reintentar»', clase: 'error' }
        : { estado: 'ERROR_TEMPORAL', texto: 'Error temporal · se reintentará', clase: 'error' };
    case 'RECHAZADA':
      return { estado: 'NECESITA_REVISION', texto: 'Necesita revisión', clase: 'aviso' };
    case 'SINCRONIZADA':
      return { estado: 'SINCRONIZADO', texto: 'Guardado en el servidor', clase: 'exito-suave' };
  }
}

/** Texto que acompaña SIEMPRE a una operación recién guardada: nunca un «Guardado» ambiguo. */
export const GUARDADO_EN_DISPOSITIVO = 'Guardado en este dispositivo · pendiente de sincronizar';
export const GUARDADO_EN_SERVIDOR = 'Guardado en el servidor';
